import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import {
  AccountSettingsDropdown,
  IItemProps,
} from "@pages/account-settings/account-settings-dropdown";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";
import { getUnitHeader } from "@pages/account-settings/account-settings-utils";
import { Skeleton } from "@mui/material";
import { BrowserUtils } from "@stellar/utils";
import { transmitSettingsToParent } from "@utils/account-settings-utils";
import { isFetchingUserSettingsSelector, userSettingsSelector } from "@store/settings/settings-selector";
import { updateUserSetting } from "@store/settings/settings-thunks";

type UnitType = SettingsApiTypes.UnitMeasurement;

/**
 * All the unit types mapped to their description
 * "metric" and "imperial" is allowed to have them as subheader
 */
const units: Record<UnitType | "metric" | "imperial", IItemProps<UnitType>> = {
  metric: {
    isHeader: true,
    // Using meters as value here just to be allowed in the list as subheader, no use of this value
    value: "meters",
    label: "METRIC",
  },
  meters: {
    value: "meters",
    label: "Meters",
    description: "(2.345 m)",
  },
  millimeters: {
    value: "millimeters",
    label: "Millimeters",
    description: "(2345 mm)",
  },
  imperial: {
    isHeader: true,
    // Using decimalFeet as value here just to be allowed in the list as subheader, no use of this value
    value: "decimalFeet",
    label: "IMPERIAL",
  },
  feetFractionalInches: {
    value: "feetFractionalInches",
    label: "Feet + fractional inches",
    description: "(7' 8 5/16\")",
  },
  decimalFeet: {
    value: "decimalFeet",
    label: "Decimal feet",
    description: "(7.694')",
  },
  decimalUSSurveyFeet: {
    value: "decimalUSSurveyFeet",
    label: "Decimal US survey feet",
    description: "(7.694 ft US)",
  },
  fractionalInches: {
    value: "fractionalInches",
    label: "Fractional inches",
    description: '(92 5/16")',
  },
  decimalInches: {
    value: "decimalInches",
    label: "Decimal inches",
    description: '(92.32")',
  },
};

/** A simple unit dropdown. When opened the user can chose between the available units */
export function AccountUnitSelector(): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const [selectedUnit, setSelectedUnit] = useState<IItemProps<UnitType>>(
    units.meters
  );
  const [updatedModifiedAt, setUpdatedModifiedAt] = useState<string>("0");
  const { handleErrorWithToast } = useErrorContext();
  const userSettings = useAppSelector(userSettingsSelector);
  const isFetchingUserSettings = useAppSelector(isFetchingUserSettingsSelector);
  const dispatch = useAppDispatch();

  // Initialize the selected unit from user settings when available
  useEffect(() => {
    if (!userSettings?.unit_measurement) {
      if (!loggedInUser) {
        // For guest user, try to get the unit setting from local storage
        const localUnit = localStorage.getItem("unit_measurement");
        if (localUnit && SettingsApiTypes.isValidUnitMeasurement(localUnit)) {
          setSelectedUnit(units[localUnit]);
        }
      }
      return;
    }
    // Set the selected unit from user settings for logged in user
    const { value, modifiedAt } = userSettings.unit_measurement;
    setSelectedUnit(units[value]);
    setUpdatedModifiedAt(modifiedAt);
  }, [loggedInUser, userSettings]);

  async function onUnitChange(newUnit: UnitType): Promise<void> {
    trackEvent({
      name: UserProfile.changeUnit,
      props: { unit: newUnit, isUserLoggedIn: !!loggedInUser },
    });

    // Update unit in local storage for both guest and authenticated users
    localStorage.setItem("unit_measurement", newUnit);

    // Transmit settings to parent window
    if (BrowserUtils.isInIframe()) {
      transmitSettingsToParent({
        settingIdentifier: "unit_measurement",
        settingValue: newUnit,
      });
    }

    // Avoid making the API call if the user not logged in
    if (!loggedInUser) {
      setSelectedUnit(units[newUnit]);
      return;
    }

    try {
      const response = await dispatch(updateUserSetting({
        identifier: "unit_measurement",
        userId: loggedInUser.id,
        payload: {
          value: newUnit,
          modifiedAt: updatedModifiedAt,
        },
      })).unwrap();

      setSelectedUnit(units[response.setting.value as UnitType]);
      setUpdatedModifiedAt(response.setting.modifiedAt);
    } catch (error) {
      handleErrorWithToast({
        id: `updateUnit-${Date.now().toString()}`,
        title: "Failed to update the unit setting.",
        error,
      });
    }
  }

  if (isFetchingUserSettings) {
    return <Skeleton variant="rectangular" width="100%" />;
  }
  return (
    <AccountSettingsDropdown
      items={Object.values(units)}
      selectedItem={{
        label: `${getUnitHeader(selectedUnit.value)} / ${selectedUnit.label}`,
        value: selectedUnit.value,
        description: selectedUnit.description,
      }}
      onClickHandler={onUnitChange}
    />
  );
}
